/**
 * The sync engine: drain the outbox, then pull what changed. Lift's engine,
 * generalised — the server returns `changes: {entity: rows[]}` and TABLES maps
 * each entity to its Dexie table, so adding a synced table touches no code here.
 *
 * Push before pull, always: pulling first would overwrite local rows with
 * server copies that don't yet include the edits still in the outbox.
 */

import { api, ApiError, getToken } from '../api/client'
import { CURSOR_KEY, TABLES, db, getMeta, setMeta, type Entity } from '../db'
import { isOnline, watch as watchNetwork } from '../native/network'

const PUSH_BATCH = 200

export type SyncState = {
  running: boolean
  online: boolean
  pending: number
  lastSyncedAt: number | null
  lastError: string | null
}

type Listener = (s: SyncState) => void

let state: SyncState = {
  running: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  pending: 0,
  lastSyncedAt: null,
  lastError: null,
}
const listeners = new Set<Listener>()

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  fn(state)
  return () => listeners.delete(fn)
}

function emit(patch: Partial<SyncState>) {
  state = { ...state, ...patch }
  listeners.forEach((fn) => fn(state))
}

export function currentState(): SyncState {
  return state
}

async function refreshPending() {
  emit({ pending: await db.outbox.count() })
}

type PushResponse = {
  applied: string[]
  skipped: string[]
  resolved: { op_id: string; entity: Entity; row: Record<string, unknown> }[]
  errors?: { op_id: string | null; error: string }[]
}

async function pushOnce(): Promise<boolean> {
  const ops = await db.outbox.orderBy('created_at').limit(PUSH_BATCH).toArray()
  if (ops.length === 0) return false
  const res = await api<PushResponse>('/sync/push', {
    body: { ops: ops.map((o) => ({ op_id: o.op_id, entity: o.entity, op: o.op, payload: o.payload })) },
  })
  // skipped = already applied by an attempt whose response we never saw.
  const done = new Set([...(res.applied ?? []), ...(res.skipped ?? [])])
  for (const r of res.resolved ?? []) {
    done.add(r.op_id)
    await applyServerRow(r.entity, r.row, true)
  }
  for (const e of res.errors ?? []) {
    if (e.op_id) {
      done.add(e.op_id)
      console.warn('[sync] op rejected, dropping:', e.op_id, e.error)
    }
  }
  await db.outbox.bulkDelete([...done])
  await refreshPending()
  return done.size > 0
}

/** Write a server row unless a local edit to it is still queued. */
async function applyServerRow(entity: Entity, row: Record<string, unknown>, force = false) {
  const table = TABLES[entity]
  if (!table) return
  const id = row.id as string
  if (!force && (await db.outbox.where('row_id').equals(id).count()) > 0) return
  await table.put(row)
}

type PullResponse = {
  changes: Partial<Record<Entity, Record<string, unknown>[]>>
  next_cursor: number
  has_more: boolean
}

async function pullOnce(): Promise<boolean> {
  const cursor = await getMeta<number>(CURSOR_KEY, 0)
  const res = await api<PullResponse>(`/sync/pull?since=${cursor}`)
  for (const [entity, rows] of Object.entries(res.changes ?? {})) {
    for (const row of rows ?? []) await applyServerRow(entity as Entity, row)
  }
  if (res.next_cursor > cursor) await setMeta(CURSOR_KEY, res.next_cursor)
  return res.has_more
}

let inFlight: Promise<void> | null = null

export async function sync(): Promise<void> {
  if (!getToken()) return
  if (inFlight) return inFlight
  inFlight = (async () => {
    emit({ running: true, lastError: null })
    try {
      for (let i = 0; i < 20; i++) if (!(await pushOnce())) break
      for (let i = 0; i < 50; i++) if (!(await pullOnce())) break
      emit({ lastSyncedAt: Date.now(), online: true })
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        emit({ lastError: 'Signed out' })
      } else {
        emit({ lastError: err instanceof Error ? err.message : 'Sync failed' })
        void isOnline().then((online) => emit({ online }))
      }
    } finally {
      await refreshPending()
      emit({ running: false })
      inFlight = null
    }
  })()
  return inFlight
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

export function syncSoon(delayMs = 2500): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    void sync()
  }, delayMs)
}

let started = false

export function startSyncLoop(): () => void {
  if (started) return () => {}
  started = true
  const onVisible = () => { if (document.visibilityState === 'visible') void sync() }
  const unwatch = watchNetwork((online) => {
    emit({ online })
    if (online) void sync()
  })
  void isOnline().then((online) => emit({ online }))
  document.addEventListener('visibilitychange', onVisible)
  const interval = setInterval(() => {
    if (document.visibilityState === 'visible') void sync()
  }, 5 * 60 * 1000)
  void sync()
  return () => {
    started = false
    unwatch()
    document.removeEventListener('visibilitychange', onVisible)
    clearInterval(interval)
  }
}
